import { ChangeDetectorRef, Component, inject, ChangeDetectionStrategy } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, switchMap, take } from 'rxjs/operators';

import { FullCandidateActions, FullCandidateSelectors } from '@core/candidate-data';
import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogHeaderComponent,
  DialogRef,
  DividerComponent,
  GroupComponent,
  NotificationsController,
} from '@core/user-interface-legacy';

type DialogData = {
  candidateId: number;
};

@Component({
  selector: 'rt-re-parse-dialog',
  templateUrl: './re-parse-dialog.component.html',
  styleUrls: ['./re-parse-dialog.component.less'],
  host: {
    '[class]': "'default'",
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DialogHeaderComponent,
    DividerComponent,
    GroupComponent,
    ButtonComponent,
    ButtonLoadingDirective,
  ],
})
export class ReParseDialogComponent implements DialogComponentI<DialogData, void> {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly dialogRef = inject<DialogRef<ReParseDialogComponent, void>>(DialogRef);
  private readonly i18n = inject(I18nService);
  private readonly notificationsCtrl = inject(NotificationsController);
  protected readonly cdRef = inject(ChangeDetectorRef);

  public title: string = this.i18n.translate('app.candidate.cv_reparse_dialog.title');
  public message: string = this.i18n.translate('app.candidate.cv_reparse_dialog.message');
  public agreeLabel: string = this.i18n.translate('app.candidate.cv_reparse.agree_label');
  public cancelLabel: string = this.i18n.translate('app.common.cancel');
  public loading: boolean = false;

  private candidateId: number;

  public setDialogData(data: DialogData, triggerUpdate: boolean): void {
    this.candidateId = data.candidateId;
    if (triggerUpdate) {
      this.cdRef.detectChanges();
    }
  }

  public confirm(): void {
    this.loading = true;
    this.cdRef.detectChanges();
    this.store.dispatch(FullCandidateActions.reparseCv({ payload: { id: this.candidateId } }));
    this.actions$
      .pipe(
        ofType(FullCandidateActions.reparseCvSuccess),
        filter(({ payload }) => payload.data.id === this.candidateId),
        switchMap(() =>
          this.store.select(FullCandidateSelectors.byIdWithReferences(this.candidateId)),
        ),
        filterBoolean,
        take(1),
      )
      .subscribe(() => {
        this.notificationsCtrl.success({
          title: this.i18n.translate('app.candidate.cv_reparse.success.title'),
          message: this.i18n.translate('app.candidate.cv_reparse.success.message'),
        });
        this.dialogRef.close();
      });
  }

  public cancel(): void {
    this.dialogRef.close();
  }
}
